/**
 * 全景热力矩阵 — HeatMatrix（对齐 theme.css §热力矩阵 .matrix/.cell l0-l4）。
 *
 * - 行首 sticky 左冻结话题列；单元格高 40px；色 + 数值双编码
 * - 色阶统一走 constants.HEAT_LEVELS + heatLevel()（对齐 theme.css §1.3），禁散落硬编码 hex
 * - tooltip 明细：score / prev_score / 环比 / sample_n；sample_n<3（或 status=reference）标"参考值"（BR4）
 * - lost 单元格：l1 色 + 0 值 + 失联标记（AR1 联动）
 * - sortByGap：差距最大优先（话题内最小得分升序，同分按环比最大跌幅）
 * - highlightPlatform：钻取态列头高亮 + 徽章
 */
import { useMemo, type ReactNode } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import Badge from "./Badge";
import { HEAT_LEVELS, heatLevel } from "../utils/constants";
import type { MatrixCell } from "../types";

export interface HeatMatrixProps {
  /** 话题列表（行）。 */
  topics: string[];
  /** 平台列表（列）。 */
  platforms: string[];
  /** 单元格数据（GET /api/ai-visibility/matrix cells）。 */
  cells: MatrixCell[];
  /** 钻取态高亮平台（列头高亮 + 徽章）。 */
  highlightPlatform?: string;
  /** 点击单元格回调（话题 + 平台，联动趋势图）。 */
  onCellClick?: (topic: string, platform: string) => void;
  /** 差距最大优先排序（话题最小得分升序，同分按环比最大跌幅）。 */
  sortByGap?: boolean;
  /** 当前选中话题（行联动，钻取/趋势用）。 */
  selectedTopic?: string;
  /** 当前选中平台（单元格描边，钻取/趋势用）。 */
  selectedPlatform?: string;
}

/** 单元格查找键。 */
const cellKey = (topic: string, platform: string) => `${topic}||${platform}`;

/** 图例（对齐 theme.css .matrix-legend，色块取 HEAT_LEVELS）。 */
const LEGEND: { level: 1 | 2 | 3 | 4; label: string }[] = [
  { level: 1, label: "1–20 差" },
  { level: 2, label: "21–50 中" },
  { level: 3, label: "51–80 良" },
  { level: 4, label: "81–100 优" },
];

/** 单元格 tooltip 明细（score/prev_score/环比/sample_n/参考值/失联）。 */
function cellTooltip(
  cell: MatrixCell | undefined,
  topic: string,
  platform: string,
): ReactNode {
  if (!cell) return `${platform} · ${topic} · 无数据`;
  const delta =
    cell.score != null && cell.prev_score != null
      ? cell.score - cell.prev_score
      : null;
  const lines = [
    `${platform} · ${topic}`,
    `提及率得分 ${cell.score ?? "—"}，上期 ${cell.prev_score ?? "—"}`,
    ...(delta !== null ? [`环比 ${delta > 0 ? "+" : ""}${delta}`] : []),
    `样本 n=${cell.sample_n}`,
    ...(cell.status === "lost" ? ["失联：本期未检出品牌（AR1 联动）"] : []),
    ...(cell.status === "reference" || cell.sample_n < 3
      ? ["参考值：样本量不足（n<3，BR4）"]
      : []),
  ];
  return (
    <Box sx={{ whiteSpace: "pre-line", fontSize: 12 }}>{lines.join("\n")}</Box>
  );
}

export default function HeatMatrix({
  topics,
  platforms,
  cells,
  highlightPlatform,
  onCellClick,
  sortByGap = false,
  selectedTopic,
  selectedPlatform,
}: HeatMatrixProps) {
  /** 单元格查找表。 */
  const cellMap = useMemo(() => {
    const m = new Map<string, MatrixCell>();
    for (const c of cells) m.set(cellKey(c.topic, c.platform), c);
    return m;
  }, [cells]);

  /** 行排序：差距最大优先（话题内最小得分升序，同分按环比最大跌幅）。 */
  const orderedTopics = useMemo(() => {
    if (!sortByGap) return topics;
    const metric = (t: string): { min: number; maxDrop: number } => {
      let min = Infinity;
      let maxDrop = 0;
      for (const p of platforms) {
        const c = cellMap.get(cellKey(t, p));
        if (!c) continue;
        const s = c.status === "lost" ? 0 : (c.score ?? 100);
        if (s < min) min = s;
        if (c.score != null && c.prev_score != null) {
          maxDrop = Math.max(maxDrop, c.prev_score - c.score);
        }
      }
      return { min: min === Infinity ? 101 : min, maxDrop };
    };
    return [...topics].sort((a, b) => {
      const ma = metric(a);
      const mb = metric(b);
      if (ma.min !== mb.min) return ma.min - mb.min;
      return mb.maxDrop - ma.maxDrop;
    });
  }, [sortByGap, topics, platforms, cellMap]);

  /** 失联格数（卡头徽章用）。 */
  const lostCount = useMemo(
    () => cells.filter((c) => c.status === "lost").length,
    [cells],
  );

  return (
    <Box>
      {/* 矩阵本体（.matrix：边框 + 圆角 + 横向滚动） */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.paper",
          overflow: "auto",
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            fontSize: 13,
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  height: 40,
                  bgcolor: "grey.50",
                  fontSize: 12,
                  color: "text.secondary",
                  fontWeight: 600,
                  textAlign: "left",
                  px: 2,
                  minWidth: 160,
                  borderBottom: "1px solid",
                  borderColor: "lineSoft",
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                }}
              >
                话题
              </Box>
              {platforms.map((p) => {
                const highlighted = p === highlightPlatform;
                return (
                  <Box
                    component="th"
                    key={p}
                    sx={{
                      height: 40,
                      bgcolor: highlighted ? "semanticBg.info" : "grey.50",
                      fontSize: 12,
                      color: "text.secondary",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      px: 1,
                      borderBottom: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    {p}
                    {highlighted && (
                      <Badge
                        label="钻取"
                        variant="info"
                        size="small"
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box component="tbody">
            {orderedTopics.map((t) => (
              <Box component="tr" key={t}>
                {/* 行首话题列：sticky 左冻结 */}
                <Box
                  component="th"
                  sx={{
                    width: 160,
                    minWidth: 160,
                    textAlign: "left",
                    px: 2,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "text.primary",
                    bgcolor:
                      selectedTopic === t ? "brand.50" : "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "lineSoft",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                  }}
                >
                  {t}
                </Box>
                {platforms.map((p) => {
                  const c = cellMap.get(cellKey(t, p));
                  const lost = c?.status === "lost";
                  const level = lost ? 1 : heatLevel(c?.score);
                  const colors = HEAT_LEVELS[level];
                  const selected = selectedTopic === t && selectedPlatform === p;
                  return (
                    <Box
                      component="td"
                      key={p}
                      sx={{
                        p: 0,
                        borderBottom: "1px solid",
                        borderColor: "lineSoft",
                      }}
                    >
                      <Tooltip title={cellTooltip(c, t, p)} arrow>
                        <Box
                          onClick={() => onCellClick?.(t, p)}
                          sx={{
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.25,
                            fontWeight: 600,
                            fontSize: 13,
                            bgcolor: colors.bg,
                            color: colors.text,
                            cursor: onCellClick ? "pointer" : "default",
                            border: "2px solid",
                            borderColor: selected
                              ? "primary.main"
                              : "transparent",
                            boxSizing: "border-box",
                          }}
                        >
                          {lost ? (
                            <>
                              0
                              <WarningAmberOutlined sx={{ fontSize: 14 }} />
                            </>
                          ) : c?.score != null ? (
                            c.score
                          ) : (
                            "—"
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 图例（.matrix-legend） */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          mt: 1.5,
        }}
      >
        {LEGEND.map(({ level, label }) => (
          <Box
            key={level}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.5,
                bgcolor: HEAT_LEVELS[level].bg,
                border: "1px solid",
                borderColor: "lineSoft",
              }}
            />
            {label}
          </Box>
        ))}
        {lostCount > 0 && (
          <Badge label={`${lostCount} 格失联`} variant="error" />
        )}
        <Typography
          sx={{ ml: "auto", fontSize: 12, color: "text.secondary" }}
        >
          排序：差距最大优先 · 点击单元格钻取明细
        </Typography>
      </Box>
    </Box>
  );
}
